import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { formatNumber } from "@/lib/format";
import {
  CircleNotchIcon, ScalesIcon, CaretDownIcon, TrophyIcon, CompassIcon,
} from "@phosphor-icons/react";

const gradeColor = (g = "") => {
  const c = (g || "").toUpperCase();
  if (c === "A") return "#A3E635";
  if (c === "B") return "#FFFFFF";
  if (c === "C") return "#A0A0AB";
  return "#16C784";
};

const GRADE_RANK = { A: 4, B: 3, C: 2, D: 1 };

// metric config: key path, label, direction ("high"|"low"), formatter, source ("c"|"m")
const ROWS = [
  { key: "subscriber_count", label: "Subscribers", dir: "high", src: "c", fmt: formatNumber },
  { key: "view_count", label: "Total Views", dir: "high", src: "c", fmt: formatNumber },
  { key: "video_count", label: "Videos Published", dir: "none", src: "c", fmt: formatNumber },
  { key: "potential_score", label: "Channel Potential", dir: "high", src: "m", fmt: (v) => v },
  { key: "subs_per_year", label: "Subs Gained / Year", dir: "high", src: "m", fmt: formatNumber },
  { key: "avg_views_per_post", label: "Avg Views / Post", dir: "high", src: "m", fmt: formatNumber },
  { key: "median_views_per_post", label: "Median Views", dir: "high", src: "m", fmt: formatNumber },
  { key: "engagement_rate", label: "Engagement Rate", dir: "high", src: "m", fmt: (v) => `${v}%` },
  { key: "avg_likes_per_post", label: "Avg Likes / Post", dir: "high", src: "m", fmt: formatNumber },
  { key: "avg_comments_per_post", label: "Avg Comments / Post", dir: "high", src: "m", fmt: formatNumber },
  { key: "views_per_subscriber", label: "Views / Subscriber", dir: "high", src: "m", fmt: (v) => v },
  { key: "posts_per_month", label: "Posts / Month", dir: "high", src: "m", fmt: (v) => v },
  { key: "upload_cadence_days", label: "Upload Cadence (days)", dir: "low", src: "m", fmt: (v) => (v ? `${v}d` : "—") },
  { key: "subs_progress_pct", label: "Progress to 1K Subs", dir: "high", src: "m", fmt: (v) => `${v}%` },
  { key: "hours_progress_pct", label: "Progress to 4K Watch Hrs", dir: "high", src: "m", fmt: (v) => `${v}%` },
  { key: "est_monthly_revenue_high", label: "Est. Revenue Ceiling / Mo", dir: "high", src: "m", fmt: (v) => `$${formatNumber(v)}` },
];

const getVal = (ch, row) => {
  if (!ch) return undefined;
  if (row.src === "c") return ch[row.key];
  return ch.metrics ? ch.metrics[row.key] : undefined;
};

const winner = (a, b, dir) => {
  if (dir === "none") return 0;
  if (a === undefined || a === null || b === undefined || b === null) return 0;
  if (Number(a) === Number(b)) return 0;
  const aWins = dir === "high" ? Number(a) > Number(b) : Number(a) < Number(b);
  return aWins ? -1 : 1; // -1 => A, 1 => B
};

const Picker = ({ label, value, onChange, options, testid }) => (
  <div className="relative flex-1">
    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5a5a63] mb-1.5">{label}</div>
    <div className="relative">
      <select
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full appearance-none bg-[#050505] border border-[#222226] focus:border-[#16C784] text-white font-rajdhani font-semibold text-sm px-3 py-2.5 pr-9 outline-none transition-colors duration-200"
      >
        {options.map((o) => (
          <option key={o.channel_id} value={o.channel_id}>{o.title}</option>
        ))}
      </select>
      <CaretDownIcon size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#16C784] pointer-events-none" />
    </div>
  </div>
);

const ChannelHead = ({ ch }) => (
  <div className="flex flex-col items-center text-center gap-1">
    {ch?.thumbnail && (
      <img src={ch.thumbnail} alt="" referrerPolicy="no-referrer"
        className="w-12 h-12 rounded-full border-2 border-[#16C784] object-cover" />
    )}
    <div className="font-rajdhani font-bold text-sm text-white truncate max-w-[140px]">{ch?.title}</div>
    {ch?.grade && (
      <span className="font-rajdhani font-bold text-sm px-2 border rounded-sm"
        style={{ color: gradeColor(ch.grade), borderColor: gradeColor(ch.grade) + "66" }}>
        Grade {ch.grade}
      </span>
    )}
  </div>
);

const CompareChannels = ({ channels, loading, activeId }) => {
  const [aId, setAId] = useState(null);
  const [bId, setBId] = useState(null);

  useEffect(() => {
    if (!channels.length) return;
    const first = activeId && channels.some((c) => c.channel_id === activeId) ? activeId : channels[0].channel_id;
    const second = channels.find((c) => c.channel_id !== first)?.channel_id || first;
    setAId((prev) => prev || first);
    setBId((prev) => prev || second);
  }, [channels, activeId]);

  const chA = useMemo(() => channels.find((c) => c.channel_id === aId), [channels, aId]);
  const chB = useMemo(() => channels.find((c) => c.channel_id === bId), [channels, bId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="compare-loading">
        <CircleNotchIcon size={34} className="text-[#16C784] spin-slow" />
      </div>
    );
  }

  if (channels.length < 2) {
    return (
      <div data-testid="compare-empty" className="py-20 text-center">
        <ScalesIcon size={34} className="text-[#5a5a63] mx-auto mb-3" />
        <p className="font-mono text-xs text-[#5a5a63] max-w-md mx-auto">
          Connect at least two channels to benchmark them side by side.
        </p>
      </div>
    );
  }

  // tally wins
  let aWins = 0, bWins = 0;
  ROWS.forEach((row) => {
    const w = winner(getVal(chA, row), getVal(chB, row), row.dir);
    if (w === -1) aWins++; else if (w === 1) bWins++;
  });
  const gradeW = winner(GRADE_RANK[(chA?.grade || "").toUpperCase()], GRADE_RANK[(chB?.grade || "").toUpperCase()], "high");
  if (gradeW === -1) aWins++; else if (gradeW === 1) bWins++;

  return (
    <motion.div
      data-testid="compare-channels"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative bg-[#0C0C0E] border border-[#16C784]/30 p-5 md:p-6"
      style={{ boxShadow: "0 0 15px rgba(22, 199, 132,0.08), inset 0 1px 0 rgba(255,255,255,0.05)" }}
    >
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-full border border-[#16C784]/50 flex items-center justify-center">
          <ScalesIcon size={20} className="text-[#16C784]" weight="fill" />
        </div>
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#A3E635]">// Head-To-Head</div>
          <h2 className="font-rajdhani font-bold text-xl tracking-wide">COMPARE CHANNELS</h2>
        </div>
      </div>

      <div className="flex items-end gap-3 mb-5">
        <Picker label="Channel A" value={aId} onChange={setAId} options={channels} testid="compare-select-a" />
        <div className="font-rajdhani font-bold text-[#5a5a63] pb-2.5">VS</div>
        <Picker label="Channel B" value={bId} onChange={setBId} options={channels} testid="compare-select-b" />
      </div>

      {/* headline win tally */}
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 border border-[#222226] bg-[#050505] p-4 mb-4">
        <ChannelHead ch={chA} />
        <div className="text-center">
          <div className="font-mono text-[9px] uppercase tracking-wider text-[#5a5a63]">Metrics Won</div>
          <div className="font-rajdhani font-bold text-2xl">
            <span style={{ color: aWins >= bWins ? "#A3E635" : "#A0A0AB" }} data-testid="compare-a-wins">{aWins}</span>
            <span className="text-[#5a5a63] mx-1.5">·</span>
            <span style={{ color: bWins >= aWins ? "#A3E635" : "#A0A0AB" }} data-testid="compare-b-wins">{bWins}</span>
          </div>
          {aWins !== bWins && (
            <div className="flex items-center justify-center gap-1 mt-1 font-mono text-[9px] uppercase tracking-wider text-[#16C784]">
              <TrophyIcon size={11} weight="fill" /> {(aWins > bWins ? chA?.title : chB?.title)?.slice(0, 18)}
            </div>
          )}
        </div>
        <ChannelHead ch={chB} />
      </div>

      {/* metric rows */}
      <div className="divide-y divide-[#1a1a1e] border border-[#222226]">
        {ROWS.map((row) => {
          const va = getVal(chA, row);
          const vb = getVal(chB, row);
          const w = winner(va, vb, row.dir);
          const has = (v) => v !== undefined && v !== null;
          return (
            <div key={row.key} data-testid={`compare-row-${row.key}`} className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 px-3 py-2.5">
              <div className={`text-right font-rajdhani font-bold text-base ${w === -1 ? "text-[#A3E635]" : "text-white"}`}>
                {has(va) ? row.fmt(va) : "—"}
              </div>
              <div className="text-center font-mono text-[10px] uppercase tracking-wider text-[#b8b8c2] w-40 md:w-48 truncate px-1">
                {row.label}
              </div>
              <div className={`text-left font-rajdhani font-bold text-base ${w === 1 ? "text-[#A3E635]" : "text-white"}`}>
                {has(vb) ? row.fmt(vb) : "—"}
              </div>
            </div>
          );
        })}
      </div>

      {/* Niche & revenue comparison */}
      {(chA?.niche_analysis || chB?.niche_analysis) && (
        <div data-testid="compare-niche" className="mt-4 border border-[#222226]">
          <div className="flex items-center gap-2 px-3 py-2 bg-[#050505] border-b border-[#1a1a1e] text-[11px] font-bold uppercase tracking-[0.18em] text-[#16C784]">
            <CompassIcon size={14} weight="fill" /> Niche &amp; Revenue
          </div>
          {[
            { label: "Niche", get: (c) => c?.niche_analysis?.niche },
            { label: "Revenue Opportunity", get: (c) => c?.niche_analysis?.revenue_opportunity },
            { label: "Est. RPM (niche)", get: (c) => c?.niche_analysis?.estimated_rpm },
            { label: "Competition", get: (c) => (c?.niche_analysis?.competition_level ? `${c.niche_analysis.competition_level} saturation` : null) },
          ].map((r) => (
            <div key={r.label} data-testid={`compare-niche-${r.label}`} className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 px-3 py-2.5 border-b border-[#1a1a1e] last:border-b-0">
              <div className="text-right font-rajdhani font-semibold text-sm text-white">{r.get(chA) || "—"}</div>
              <div className="text-center font-mono text-[10px] uppercase tracking-wider text-[#b8b8c2] w-40 md:w-48 px-1">{r.label}</div>
              <div className="text-left font-rajdhani font-semibold text-sm text-white">{r.get(chB) || "—"}</div>
            </div>
          ))}
        </div>
      )}

      <p className="font-mono text-[10px] text-[#5a5a63] mt-3">
        Green = the stronger channel for that metric. Grade, revenue &amp; niche estimates come from each channel's last full scan — run a scan on both for the fullest comparison.
      </p>
    </motion.div>
  );
};

export default CompareChannels;
